import { useEffect, useState, useRef } from 'react';
import { useTour } from '@reactour/tour';
import { useSelector } from "react-redux";
import { useTheme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { LineOfBusinessEnum } from '../../../constants/BusinessConstants';


export default function PageTour({ prop, isRelaunch = false }) {
  const { setIsOpen, setSteps, setCurrentStep } = useTour();
  const timeoutsRef = useRef(new Set());
  const isMountedRef = useRef(true);

  const isProfileUpdateInfoAcknowledged = useSelector(
    (state) => state.authState.userData?.data?.user_info?.is_profile_update_info_acknowledged
  );
  const walkThroughDetail = useSelector(
    (state) => state.drawerSubNav.walkThroughDetail?.page_names
  );

  const companySettings = useSelector(state => state.authState.userData?.data?.company_settings);
  const isO2prescriptionModule = companySettings?.find(val => val.module_id == 91)
  const isQuoteModuleEnable = companySettings?.find(val => val.module_id == 89)

  const storedTour = JSON.parse(localStorage.getItem('toursShown') || '{}');

  const [isTourShown, setIsTourShown] = useState({
    PatientsTour: false,
    OrdersTour: false,
    PatientDetailsTabTour: false,
    OrderDetailsTabTour: false,
    NewOrderTour: false,
    newOrderPatientTabTour: false,
    ...storedTour,
  });
  
  // helper function to handle waiting for elements before running tour
  const waitForElements = (selectors, timeout = 10000) => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      let timeoutId = null;
      
      const checkElements = () => {
        if (!isMountedRef.current) {
          resolve(false);
          return;
        }
        
        const allElementsPresent = selectors.every(selector => {
          const element = document.querySelector(selector);
          return element !== null;
        });
        
        if (allElementsPresent) {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutsRef.current.delete(timeoutId);
          }
          resolve(true);
        } else if (Date.now() - startTime > timeout) {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutsRef.current.delete(timeoutId);
          }
          resolve(false);
        } else {
          timeoutId = setTimeout(checkElements, 100);
          timeoutsRef.current.add(timeoutId);
        }
      };
      
      checkElements();
    });
  };

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      timeoutsRef.current.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      timeoutsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const updateTourState = async () => {
      let latestTourState = { ...isTourShown };
  
      if (Array.isArray(walkThroughDetail)) {
        walkThroughDetail.forEach((detail) => {
          const matchingKey = `${detail}Tour`;
          if (latestTourState.hasOwnProperty(matchingKey)) {
            latestTourState[matchingKey] = true;
          }
        });
  
        setIsTourShown(latestTourState);
        localStorage.setItem('toursShown', JSON.stringify(latestTourState));
      }
  
      const tourKey = `${prop}Tour`;
      const isTourAlreadyShown = latestTourState[tourKey];
  
      if ((!isTourAlreadyShown || isRelaunch) && walkThroughDetail) {
        let hasReportIssueButton = false;
        
        if (isRelaunch) {
          if (prop === 'OrderDetailsTab') {
            hasReportIssueButton = !!document.querySelector('.reportIssueButton');
          }
        } else {
          try {
            if (prop === 'OrderDetailsTab') {
              hasReportIssueButton = await waitForElements(['.reportIssueButton'], 4000);
            } else if (prop === 'PatientDetailsTab') {
              const selectorsToWaitFor = ['.placeOrderButton', '.managePatientButton'];
              await waitForElements(selectorsToWaitFor, 10000);
            }
          } catch (error) {
            console.warn('Tour element waiting failed:', error);
          }
        }
        
        const steps = getTourSteps(prop, isO2prescriptionModule, isQuoteModuleEnable, hasReportIssueButton);
        setSteps(steps);
        setCurrentStep(0);
  
        if (!isProfileUpdateInfoAcknowledged || isRelaunch) {
          setIsOpen(true);
  
          setIsTourShown((prevState) => {
            const updatedTourState = { ...prevState, [tourKey]: true };
            localStorage.setItem('toursShown', JSON.stringify(updatedTourState));
            return updatedTourState;
          });
        }
      }
    };
  
    updateTourState();
  }, [isRelaunch, setIsOpen, prop, setCurrentStep, isProfileUpdateInfoAcknowledged, walkThroughDetail]);

  return null;
}

export function useTourModalStyles() {
  const theme = useTheme();
  return {
    popover: (base, state) => ({
      ...base,
      boxShadow: theme.shadow.sm,
      top: theme.spacing(-3),
      padding: theme.spacing('lg', 'xxl', 'xxl'),
      '--reactour-accent': theme.palette.warning.main,
      borderRadius: theme.radius.sm,
      ...doArrow(state.position, state.verticalAlign, state.horizontalAlign, theme.palette.background.paper),
    }),
    controls: (base) => ({ 
      ...base, 
      margin: theme.spacing('md', -8, -8), 
      backgroundColor: theme.palette.warning.main, 
      padding: theme.spacing('xxs'),
      borderRadius: `0 0 ${theme.radius.sm} ${theme.radius.sm}`,
    }),
    close: (base) => ({ ...base, right: 'auto', right: 16, top: 14 }),
    maskWrapper: (base) => ({ ...base, color: theme.palette.grey[900], opacity: '0.1' }), 
    badge: (base) => ({ ...base, left: 'auto', right: '-0.8125em', display: 'none' }),
  };
}

const oppositeSide = {
  top: "bottom",
  bottom: "top",
  right: "left",
  left: "right",
};


export function doArrow(position, verticalAlign, horizontalAlign) {
  const theme = useTheme();
  const color = theme.palette.common.white;

  if (!position || position === "custom") {
    return {};
  }

  const width = 16;
  const height = 12;
  const isVertical = position === "top" || position === "bottom";
  const spaceFromSide = 32;
  const topHeight = 28;

  const obj = {
    [`--rtp-arrow-${isVertical ? oppositeSide[horizontalAlign] : verticalAlign
      }`]: height + spaceFromSide + "px",
    [`--rtp-arrow-${oppositeSide[position]}`]: position !== 'top' ? -height + "px" : -topHeight + "px",
    [`--rtp-arrow-border-${isVertical ? "left" : "top"}`]: `${width / 2
      }px solid transparent`,
    [`--rtp-arrow-border-${isVertical ? "right" : "bottom"}`]: `${width / 2
      }px solid transparent`,
    [`--rtp-arrow-border-${position}`]: `${height}px solid ${color}`,
  };
  return obj;
}

export const getTourSteps = (page, isO2prescriptionModule=false, isQuoteModuleEnable=false, hasReportIssueButton=false) => { 
  switch (page) {
    case 'Patients':
      return [
        {
          selector: '.toast-container',
          content: `View and select filters to refine the ${LineOfBusinessEnum.patientToMember.toLowerCase()} list.`,
        },
        {
          selector: '.routing-container',
          content: `Switch between viewing ${LineOfBusinessEnum.patientToMember.toLowerCase()} information in a list or table.`,
        },
        {
          selector: '.columnClass',
          content: `Update column visibility to show or hide ${LineOfBusinessEnum.patientToMember.toLowerCase()} details.`,
        },
        {
          selector: '.new-patient-button',
          content: `Create a new ${LineOfBusinessEnum.patientToMember.toLowerCase()} without placing an order.`,
          actionAfter: () => {
            document.querySelector('.routing-container .MuiTab-root:first-child')?.click();
          }
        },
        {
          selector: '.export-button',
          content: 'Download this list as a CSV spreadsheet.'
        }, 
        {
          selector: '.idg-export-button',
          content: 'Download your detailed IDG report.'
        }
      ];
    case 'Orders':
      return [
        {
          selector: '.toast-container',
          content: 'View and select filters to refine the order list.',
        },
        {
          selector: '.routing-container',
          content: 'Switch between viewing order information in a list or table.',
        },
        {
          selector: '.columnClass',
          content: 'Update column visibility to show or hide order details.',
        },
        {
          selector: '.new-order-button',
          content: `Create a new order for a ${LineOfBusinessEnum.patientToMember.toLowerCase()}.`,
          actionAfter: () => {
            document.querySelector('.routing-container .MuiTab-root:first-child')?.click();
          }
        },
        {
          selector: '.export-button',
          content: 'Download this list as a CSV spreadsheet.'
        }, 
      ];
    case 'OrderDetailsTab':
      const orderDetailSteps = [
        {
          selector: 'button:has(> .Documents)',
          content: isO2prescriptionModule ? 'View and upload files and prescriptions.' : 'View and upload files.'
        }
      ];
      
      // Only add the report issue step if the button was found 
      if (hasReportIssueButton) {
        orderDetailSteps.push({
          selector: '.reportIssueButton',
          content: 'Report an issue with this order.'
        });
      }
      
      return orderDetailSteps;
    case 'PatientDetailsTab': {
      const steps = [
        {
          selector: 'button:has(> .Documents)',
          content: isO2prescriptionModule
            ? 'View and upload files and prescriptions.'
            : 'View and upload files.',
        },
         {
          selector: '.placeOrderButton',
          content: `Choose which type of order to place for this ${LineOfBusinessEnum.patientToMember.toLowerCase()}.`
        },
        {
          selector: '.managePatientButton',
          content: `Access ${LineOfBusinessEnum.patientToMember.toLowerCase()} management features suchs as editing and transferring a ${LineOfBusinessEnum.patientToMember.toLowerCase()} or reporting an issue.`
        },
      ];

      return steps;
    }
    case 'newOrderPatientTab':
      return [
        {
          selector: '.quoteTab',
          content: `Request a quote for this ${LineOfBusinessEnum.patientToMember.toLowerCase()}.`
        }
      ]
    case 'NewOrder':
      return [
        {
          selector: '.equipmentField',
          content: 'Type to display all equipment matching the text or select a specific equipment item.'
        },
        {
          selector: '.diagnosisField',
          content: 'You can also search diagnosis codes this way.'
        }
      ]
    case 'NewOrderConfirmationTab':
      const dateOptionsFetched = parseInt(localStorage.getItem('orderRequestedDatesFetched') || '0');
      const popoverTitle = "DMETrack Update for Order Coordination";
      let steps = [
          {
              selector: "#orderNotes",
              content: ()=> (
                <Box>
                  <Typography fontWeight="semiBold">{popoverTitle}</Typography><br/>
                  <Typography>Include any additional notes for your DME provider. Please note that the schedule request is tentative and subject to provider scheduling.</Typography>
                </Box>
              ),
          },
      ];
      if(dateOptionsFetched >= 1 ){
        steps.unshift({
            selector: "#requested-schedule-time-section",
            content: ()=> (
              <Box>
                <Typography fontWeight="semiBold">{popoverTitle}</Typography><br/>
                <Typography>Choose a desired delivery window. Your requested date and time will be shared with the provider.</Typography>
              </Box>
            ),
        });
      }
      if(dateOptionsFetched > 1){
        steps.unshift({
          selector: "#requested-schedule-date-section",
          content: ()=> (
              <Box>
                <Typography fontWeight="semiBold">{popoverTitle}</Typography><br/>
                <Typography>Choose the day you’d like this order delivered.</Typography>
              </Box>
            ),
        });
      }
      return steps;
    default:
      return [
        {
          selector: '.generic-info',
          content: 'This is a generic tour.'
        },
      ];
  }
}